import React, { useState, useEffect } from 'react';
import { Eye, Mail, Phone, Send } from 'lucide-react';
import api from '../../lib/api';
import { formatPrice, formatDate } from '../../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [messageForm, setMessageForm] = useState({ subject: '', message: '' });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/admin/customers');
      setCustomers(response.data.customers || []);
    } catch (error) {
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const handleViewCustomer = async (customer) => {
    setSelectedCustomer(customer);
    try {
      const response = await api.get('/admin/orders');
      const orders = response.data.filter(o => o.user_id === customer.id);
      setCustomerOrders(orders);
    } catch (error) {
      toast.error('Failed to fetch customer orders');
    }
    setIsDialogOpen(true);
  };

  const handleSendMessage = (customer) => {
    setSelectedCustomer(customer);
    setMessageForm({ subject: '', message: '' });
    setIsMessageDialogOpen(true);
  };

  const handleSubmitMessage = async () => {
    if (!messageForm.subject || !messageForm.message) {
      toast.error('Please fill in subject and message');
      return;
    }

    try {
      await api.post('/admin/send-message', {
        customer_email: selectedCustomer.email,
        subject: messageForm.subject,
        message: messageForm.message
      });
      toast.success('Message sent successfully');
      setIsMessageDialogOpen(false);
      setMessageForm({ subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="font-serif text-4xl mb-2">Customer Management</h1>
          <p className="text-gray-600">{customers.length} customers</p>
        </div>

        {/* Customers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {customers.map((customer) => (
            <div
              key={customer.id}
              className="bg-white border border-gray-200 p-6 hover:border-black transition-colors cursor-pointer"
              onClick={() => handleViewCustomer(customer)}
              data-testid={`customer-card-${customer.id}`}
            >
              <div className="mb-4">
                <h3 className="font-medium text-lg mb-1">{customer.name}</h3>
                <p className="text-sm text-gray-600">{customer.email}</p>
                {customer.phone && (
                  <p className="text-sm text-gray-600 mt-1">{customer.phone}</p>
                )}
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Orders:</span>
                  <span className="font-medium">{customer.order_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Spent:</span>
                  <span className="font-medium">{formatPrice(customer.total_spent || 0, 'USD')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Joined:</span>
                  <span className="font-medium">{formatDate(customer.created_at)}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSendMessage(customer);
                  }}
                  className="flex items-center gap-2 text-sm hover:underline"
                  data-testid={`send-message-${customer.id}`}
                >
                  <Send className="w-4 h-4" />
                  Send Message
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    window.location.href = `mailto:${customer.email}`;
                  }}
                  className="flex items-center gap-2 text-sm hover:underline"
                >
                  <Mail className="w-4 h-4" />
                  Email
                </button>
                {customer.phone && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      window.location.href = `tel:${customer.phone}`;
                    }}
                    className="flex items-center gap-2 text-sm hover:underline"
                  >
                    <Phone className="w-4 h-4" />
                    Call
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Customer Detail Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                {selectedCustomer?.name}
              </DialogTitle>
            </DialogHeader>

            {selectedCustomer && (
              <div className="space-y-6 mt-4">
                {/* Contact Info */}
                <div>
                  <h3 className="font-medium mb-3">Contact Information</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Email:</strong> {selectedCustomer.email}</p>
                    {selectedCustomer.phone && <p><strong>Phone:</strong> {selectedCustomer.phone}</p>}
                    <p><strong>Customer Since:</strong> {formatDate(selectedCustomer.created_at)}</p>
                  </div>
                </div>

                {/* Order History */}
                <div>
                  <h3 className="font-medium mb-3">Order History ({customerOrders.length})</h3>
                  {customerOrders.length === 0 ? (
                    <p className="text-gray-600 text-sm">No orders yet</p>
                  ) : (
                    <div className="space-y-3">
                      {customerOrders.map((order) => (
                        <div key={order.id} className="border border-gray-200 p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium">{order.order_number}</p>
                              <p className="text-sm text-gray-600">{formatDate(order.created_at)}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium">{formatPrice(order.total, order.currency)}</p>
                              <span className={`text-xs px-2 py-1 rounded ${
                                order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                                order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {order.status}
                              </span>
                            </div>
                          </div>
                          <div className="text-sm text-gray-600">
                            {order.items.length} item(s)
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-4">
                  <Button
                    onClick={() => window.location.href = `mailto:${selectedCustomer.email}`}
                    className="flex-1 bg-black text-white hover:bg-gray-900"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Send Email
                  </Button>
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Send Message Dialog */}
        <Dialog open={isMessageDialogOpen} onOpenChange={setIsMessageDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                Send Message to {selectedCustomer?.name}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  value={messageForm.subject}
                  onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                  placeholder="Enter subject"
                />
              </div>

              <div>
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  value={messageForm.message}
                  onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                  rows={8}
                  placeholder="Write your message here..."
                />
              </div>

              <div className="flex gap-4">
                <Button
                  onClick={handleSubmitMessage}
                  className="flex-1 bg-black text-white hover:bg-gray-900"
                  data-testid="send-message-button"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
                <Button variant="outline" onClick={() => setIsMessageDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminCustomers;